'use client';

import { useState, useEffect } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ArrowLeft, ArrowRight, MapPin } from 'lucide-react';
import { motion } from 'framer-motion';
import { useAtomValue } from 'jotai';
import { useQuery } from '@tanstack/react-query';
import { usePostHog } from 'posthog-js/react';

import { StepsIndicator } from '@/components/glitch-ui/steps-indicator';
import { CampaignLocations } from '@/components/campaign/onboarding/campaign-onboarding-locations';
import { SourceCampaignPicker } from '@/components/campaign/source-campaign-picker';
import { Location } from '@/lib/types';
import { useToast } from '@/hooks/useToast';
import useLoadingOverlay from '@/hooks/useLoadingOverlay';
import { organizationDataAtom } from '@/store';
import {
  businessQueryOptions,
  campaignsQueryOptions,
  useCreateOptimisationCampaignMutation,
} from '@/lib/query-options';

export const Route = createFileRoute('/_authenticated/new/_layout/campaign/location')({
  component: LocationCampaign,
});

enum LOCATION_STEP {
  SELECT_SOURCE = 'SELECT_SOURCE',
  SELECT_LOCATION = 'SELECT_LOCATION',
}

function LocationCampaign() {
  const navigate = useNavigate({ from: Route.fullPath });
  const { toast } = useToast();
  const posthog = usePostHog();
  const organizationData = useAtomValue(organizationDataAtom);

  const { data: business } = useQuery({
    ...businessQueryOptions(organizationData?.id || 0),
    enabled: !!organizationData?.id,
  });

  // Source campaign picker — eligible sources are existing campaigns whose
  // googleadsaccount_id will be inherited by the new Location campaign.
  const { data: campaigns } = useQuery({
    ...campaignsQueryOptions(organizationData?.id),
    enabled: !!organizationData?.id,
  });
  const eligibleSources = (campaigns || []).filter(
    (c) => c.goal === 'prospecting' || c.goal === 'standard' || c.goal === 'onboarding'
  );

  const [currentStep, setCurrentStep] = useState<LOCATION_STEP>(LOCATION_STEP.SELECT_SOURCE);
  const [sourceCampaignId, setSourceCampaignId] = useState<number | null>(null);
  const [locations, setLocations] = useState<Location[]>([]);
  const [stepValid, setStepValid] = useState(false);
  // Seed once from business locations. CampaignLocations adopts a late-arriving `locations`
  // prop itself (#4794), so no remount key is needed here anymore.
  const [locationsSeeded, setLocationsSeeded] = useState(false);

  useEffect(() => {
    if (locationsSeeded) return;
    if (business?.target_locations !== undefined) {
      if (business.target_locations.length > 0) {
        // Location campaign is single-select: seed with the first detected location only.
        setLocations(business.target_locations.slice(0, 1));
      }
      setLocationsSeeded(true);
    }
  }, [business?.target_locations]);

  const createMutation = useCreateOptimisationCampaignMutation();
  useLoadingOverlay(createMutation.isPending);

  // Step validation
  useEffect(() => {
    if (currentStep === LOCATION_STEP.SELECT_SOURCE) {
      setStepValid(sourceCampaignId !== null);
    } else {
      setStepValid(locations.length === 1);
    }
  }, [currentStep, sourceCampaignId, locations.length]);

  const handleBack = () => {
    if (currentStep === LOCATION_STEP.SELECT_SOURCE) {
      navigate({ to: '/new/campaign' });
      return;
    }
    setCurrentStep(LOCATION_STEP.SELECT_SOURCE);
  };

  const handleNext = async () => {
    if (currentStep === LOCATION_STEP.SELECT_SOURCE) {
      setCurrentStep(LOCATION_STEP.SELECT_LOCATION);
      return;
    }
    if (!business?.id || !organizationData?.id || !sourceCampaignId || locations.length !== 1) {
      return;
    }

    // Submit from parent's already-filtered single-select state. Do NOT call
    // CampaignLocations.approveLocations(): that helper writes its own
    // (multi-select) internal state back over our parent state, which would
    // overwrite the single-select filter applied by setLocations below.
    const picked = locations[0];
    try {
      posthog?.capture('location_campaign_creation_submitted', {
        business_id: business.id,
        source_campaign_id: sourceCampaignId,
        location_id: picked.criteria_id,
      });
      // Source campaign owns the authoritative business_id. The org-default
      // `business.id` only lines up when an org has a single business.
      const pickedSource = eligibleSources.find((c) => c.id === sourceCampaignId);
      const response = await createMutation.mutateAsync({
        organizationId: organizationData.id,
        idempotencyKey: crypto.randomUUID(),
        payload: {
          campaign_type: 'location',
          source_campaign_id: sourceCampaignId,
          business_id: pickedSource?.business_id ?? business.id,
          // Backend's LocationInput schema is strict (extra="forbid"); the
          // full FE Location object carries fields the API rejects. Strip
          // to just what the schema accepts.
          target_locations: [
            {
              criteria_id: picked.criteria_id,
              name: picked.name,
              target_type: picked.target_type,
            },
          ],
          budget_amount: 0,
          location_data: {
            location_id: picked.criteria_id,
            location_name: picked.name,
            target_type: picked.target_type,
          },
        },
      });
      // Endpoint pre-creates the stub Campaign synchronously, so the row
      // exists in the DB by the time we land on the detail page — no need
      // for the old refetchWithDelayedUpdates() polling dance.
      navigate({
        to: '/campaigns/google/$campaignslug',
        params: { campaignslug: response.slug },
      });
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to create Location Campaign';
      posthog?.capture('location_campaign_creation_failed', { error_message: errorMessage });
      toast({ description: errorMessage, variant: 'destructive' });
    }
  };

  const stepIndicator = currentStep === LOCATION_STEP.SELECT_SOURCE ? 1 : 2;

  return (
    <div className="absolute top-0 left-1/2 transform -translate-x-1/2 max-w-5xl w-full space-y-6 m-auto pt-8 pb-16">
      <StepsIndicator currentStep={stepIndicator} totalSteps={2} />
      <div className="flex justify-center">
        <div className={currentStep === LOCATION_STEP.SELECT_LOCATION ? 'w-full' : 'w-[60%]'}>
          <h1 className="text-3xl font-bold text-center mb-2">Create a Location Campaign</h1>
          <p className="text-gray-700 text-center mb-8">
            {currentStep === LOCATION_STEP.SELECT_SOURCE
              ? 'Select an existing campaign to derive Google Ads context from.'
              : `Pick the geographic area to target${business?.brand_name ? ` for ${business.brand_name}` : ''}.`}
          </p>
          <Card className="p-6">
            {currentStep === LOCATION_STEP.SELECT_SOURCE && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, ease: 'easeInOut' }}
              >
                <SourceCampaignPicker
                  campaigns={eligibleSources}
                  selectedId={sourceCampaignId}
                  onSelect={setSourceCampaignId}
                  emptyState={
                    <div className="text-center py-8 text-gray-500">
                      No eligible source campaigns yet. Create a Prospecting campaign first to use as a source for
                      Location campaigns.
                    </div>
                  }
                />
              </motion.div>
            )}

            {currentStep === LOCATION_STEP.SELECT_LOCATION && (
              <CampaignLocations
                onNext={() => {}}
                setLocations={(next) => {
                  // Single-select: keep only the most recently added location.
                  setLocations(next.length > 0 ? [next[next.length - 1]] : []);
                }}
                setStepValid={setStepValid}
                locations={locations}
                businessId={business?.id}
                candidates={business?.target_locations ?? []}
                singleSelect
              />
            )}
          </Card>

          <div className="mx-auto flex w-full max-w-[614px] justify-between items-center pt-6">
            <Button
              variant="outline"
              size="lg"
              onClick={handleBack}
              className="w-[216px] py-6 flex items-center justify-center space-x-2 text-gray-700 hover:text-gray-900 border-gray-200 bg-white hover:bg-gray-50"
            >
              <ArrowLeft className="h-4 w-4" />
              <span>BACK</span>
            </Button>
            <Button
              size="lg"
              disabled={!stepValid || createMutation.isPending}
              onClick={handleNext}
              className="w-[216px] py-6 bg-[#290BD9] hover:bg-[#2309C5] shadow-lg flex items-center justify-center space-x-2"
            >
              <span>{currentStep === LOCATION_STEP.SELECT_LOCATION ? 'CREATE' : 'CONTINUE'}</span>
              {currentStep === LOCATION_STEP.SELECT_LOCATION ? (
                <MapPin className="h-4 w-4" />
              ) : (
                <ArrowRight className="h-4 w-4" />
              )}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
